import { useEffect, useRef, useState } from "react";
import { Volume2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { shuffle, randomFrom } from "@/lib/gameData";
import { FIRST_SOUND_ITEMS } from "@/lib/firstSoundData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function newRound(current) {
  let pick = randomFrom(FIRST_SOUND_ITEMS);
  let guard = 5;
  while (current && pick.sound === current.sound && guard-- > 0) {
    pick = randomFrom(FIRST_SOUND_ITEMS);
  }
  const options = shuffle([pick.correct, ...pick.distractors]);
  return { item: pick, options };
}

export default function FirstSound() {
  const [round, setRound] = useState(() => newRound(null));
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const timersRef = useRef([]);
  const { award } = useAuth();

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    setFeedback(null);
    setLocked(false);
    const t = setTimeout(
      () => speak(`${round.item.letter}. ${round.item.sound}.`, { rate: 0.85 }),
      200
    );
    timersRef.current.push(t);
  }, [round]);

  const pick = (word) => {
    if (locked) return;
    setLocked(true);
    setAttempts((a) => a + 1);
    if (word === round.item.correct.word) {
      setScore((s) => s + 1);
      award(10, "firstsound");
      setFeedback({ correct: true, word });
      speak(`${word}. Starts with ${round.item.sound}.`, { rate: 0.9 });
      toast.success(`Yes! ${word} starts with /${round.item.sound}/.`);
      const t = setTimeout(() => setRound((cur) => newRound(cur.item)), GAME_FEEDBACK_MS + 400);
      timersRef.current.push(t);
    } else {
      setFeedback({ correct: false, word });
      toast.error(`Not quite — listen again.`);
      const t = setTimeout(() => {
        setFeedback(null);
        setLocked(false);
      }, GAME_FEEDBACK_MS);
      timersRef.current.push(t);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid="firstsound-score"
          className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="firstsound-next"
          onClick={() => setRound((cur) => newRound(cur.item))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New sound
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Which picture starts with this sound?
        </p>
        <button
          data-testid="firstsound-play"
          onClick={() => speak(`${round.item.letter}. ${round.item.sound}.`, { rate: 0.85 })}
          className="mt-2 inline-flex items-center gap-3 rounded-full px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-bold shadow-md hover:shadow-lg transition-all"
        >
          <Volume2 className="w-5 h-5" /> Play /{round.item.sound}/
        </button>
        <div
          data-testid="firstsound-letter"
          className="mt-3 font-display text-5xl font-semibold text-cyan-600"
        >
          {round.item.letter}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {round.options.map((opt, i) => {
          const isRight = feedback && feedback.correct && feedback.word === opt.word;
          const isWrong = feedback && !feedback.correct && feedback.word === opt.word;
          return (
            <button
              key={opt.word}
              data-testid={`firstsound-option-${i}`}
              onClick={() => pick(opt.word)}
              disabled={locked}
              className={`rounded-3xl border-2 p-5 flex flex-col items-center gap-2 transition-all
                ${isRight ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrong ? "bg-rose-100 border-rose-300 text-rose-700 phonx-shake" : ""}
                ${!feedback ? "bg-white border-slate-200 text-slate-800 hover:border-cyan-400 hover:-translate-y-1 shadow-sm hover:shadow-lg" : ""}
              `}
            >
              <span className="text-5xl">{opt.emoji}</span>
              <span className="font-display text-xl font-semibold">{opt.word}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
